import React, { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Subscribe } from 'unstated';

import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import { cssMap, cx } from '@atlaskit/css';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { token } from '@atlaskit/tokens';
import { Anchor, Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import { ModalHeader, ModalBody } from '@atlaskit/modal-dialog';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import FolderOpenIcon from '@atlaskit/icon/core/folder-open';
import DefaultAppIcon from '@atlaskit/icon/core/app';
import Spinner from '@atlaskit/spinner';

import { FallbackableImage } from '@confluence/fallbackable-image';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { SelectToggleIcon } from './SelectToggleIcon.tsx';
import { useContentTitles } from './hooks/useContentTitles.ts';
import { useSubmitSuggestionActions } from './hooks/useSubmitSuggestionActions.ts';
import {
	type Suggestion,
	type SuggestionGroup,
	type SuggestionProductKey,
	PRODUCT_ICON_MAP,
	DEFAULT_PRODUCT_INFO,
} from './types/RemoteObject.ts';
import { StackedContentTypeIcons } from './StackedContentTypeIcons.tsx';

type ParentGrouping = {
	parentTitle: string;
	suggestions: Suggestion[];
};

type ReviewContentSuggestionsModalProps = {
	groups: SuggestionGroup[];
	iconUrls: string[];
	spaceKey?: string;
	spaceName?: string;
	iconPath?: string;
	onClose: () => void;
	onSaveSuccess: () => void | Promise<void>;
};

type SuggestionItemRowProps = {
	suggestion: Suggestion;
	isSelected: boolean;
	onToggleSelectItem: (id: string) => void;
	onHoverCardVisibilityChange: (isVisible: boolean) => void;
};

type SuggestionGroupRowProps = {
	parentTitle: string;
	suggestions: Suggestion[];
	isExpanded: boolean;
	isGroupSelected: boolean;
	selectedItems: Set<string>;
	toggleExpand: (parentId: number) => void;
	parentId: number;
	toggleSelectGroup: (groupSuggestions: Suggestion[]) => void;
	onToggleSelectItem: (id: string) => void;
	onGroupHoverChange: (parentId: number, isHovering: boolean) => void;
};

type ModalLeftPanelProps = {
	groupedSuggestionsWithTitles: Map<number, ParentGrouping>;
	selectedItems: Set<string>;
	expandedGroups: Set<number>;
	toggleExpand: (parentId: number) => void;
	toggleSelectGroup: (groupSuggestions: Suggestion[]) => void;
	toggleSelectItem: (itemId: string) => void;
	onGroupHoverChange: (parentId: number, isHovering: boolean) => void;
};

const i18n = defineMessages({
	bringTogetherTitle: {
		id: 'side-navigation.review-content-suggestions-modal.bring-together-title',
		defaultMessage: 'Bring together the content that matters',
		description: 'Main title on the left panel of the content suggestions modal',
	},
	bringTogetherDescription: {
		id: 'side-navigation.review-content-suggestions-modal.bring-together-description',
		defaultMessage: 'Add these suggestions and it’ll be organized in this space.',
		description: 'Description on the left panel of the content suggestions modal',
	},
	addItemsToSpace: {
		id: 'side-navigation.review-content-suggestions-modal.add-items-to-space',
		defaultMessage: 'Add {count, plural, one {# item} other {# items}} to space',
		description: 'Button label for adding selected content suggestions to space',
	},
	expandGroup: {
		id: 'side-navigation.review-content-suggestions-modal.expand-group',
		defaultMessage: 'Expand {title}',
		description: 'Accessible label for expanding a suggestion group',
	},
	collapseGroup: {
		id: 'side-navigation.review-content-suggestions-modal.collapse-group',
		defaultMessage: 'Collapse {title}',
		description: 'Accessible label for collapsing a suggestion group',
	},
	openSuggestion: {
		id: 'side-navigation.review-content-suggestions-modal.open-suggestion',
		defaultMessage: 'Open {title} in a new tab',
		description: 'Accessible label for opening a suggestion link in a new tab',
	},
	deselectGroup: {
		id: 'side-navigation.review-content-suggestions-modal.deselect-group',
		defaultMessage: 'Deselect group {title}',
		description: 'Accessible label for deselecting all items in a suggestion group',
	},
	selectGroup: {
		id: 'side-navigation.review-content-suggestions-modal.select-group',
		defaultMessage: 'Select group {title}',
		description: 'Accessible label for selecting all items in a suggestion group',
	},
	deselectItem: {
		id: 'side-navigation.review-content-suggestions-modal.deselect-item',
		defaultMessage: 'Deselect {title}',
		description: 'Accessible label for deselecting a single suggestion item',
	},
	selectItem: {
		id: 'side-navigation.review-content-suggestions-modal.select-item',
		defaultMessage: 'Select {title}',
		description: 'Accessible label for selecting a single suggestion item',
	},
	untitled: {
		id: 'side-navigation.review-content-suggestions-modal.untitled',
		defaultMessage: 'Untitled',
		description: 'Fallback when parent content has no title',
	},
	loading: {
		id: 'side-navigation.review-content-suggestions-modal.loading',
		defaultMessage: 'Loading suggestions…',
		description: 'Accessible label for the loading state in the content suggestions modal',
	},
	submitting: {
		id: 'side-navigation.review-content-suggestions-modal.submitting',
		defaultMessage: 'Adding items to space…',
		description: 'Accessible label while submitting content suggestions to the space',
	},
	hoverCardFallbackDescription: {
		id: 'side-navigation.review-content-suggestions-modal.hover-card-fallback-description',
		defaultMessage: 'Open this item in a new tab to view it in {product}.',
		description:
			'Fallback description shown in the hover card when a suggestion has no description',
	},
	openHoverCardSuggestion: {
		id: 'side-navigation.review-content-suggestions-modal.open-hover-card-suggestion',
		defaultMessage: 'Open item',
		description: 'Call to action in the hover card to open the suggestion URL',
	},
	productGoogleDocs: {
		id: 'side-navigation.review-content-suggestions-modal.product-google-docs.no-translate',
		defaultMessage: 'Google Docs',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productGoogleSheets: {
		id: 'side-navigation.review-content-suggestions-modal.product-google-sheets.no-translate',
		defaultMessage: 'Google Sheets',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productGoogleSlides: {
		id: 'side-navigation.review-content-suggestions-modal.product-google-slides.no-translate',
		defaultMessage: 'Google Slides',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productGoogleDrive: {
		id: 'side-navigation.review-content-suggestions-modal.product-google-drive.no-translate',
		defaultMessage: 'Google Drive',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productLoom: {
		id: 'side-navigation.review-content-suggestions-modal.product-loom.no-translate',
		defaultMessage: 'Loom',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productFigma: {
		id: 'side-navigation.review-content-suggestions-modal.product-figma.no-translate',
		defaultMessage: 'Figma',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productSlack: {
		id: 'side-navigation.review-content-suggestions-modal.product-slack.no-translate',
		defaultMessage: 'Slack',
		description: 'Product label shown in the review content suggestions hover card',
	},
	productLink: {
		id: 'side-navigation.review-content-suggestions-modal.product-link',
		defaultMessage: 'Link',
		description: 'Fallback product label shown in the review content suggestions hover card',
	},
	spaceAvatarAlt: {
		id: 'side-navigation.review-content-suggestions-modal.space-avatar-alt',
		defaultMessage: 'Avatar for {spaceName}',
		description: 'Alternative text for the previewed space avatar in the right panel header',
	},
});

function getSuggestionProductInfo(url: string): {
	productKey: SuggestionProductKey;
	iconUrl: string;
} {
	const productInfo = PRODUCT_ICON_MAP.find((entry) => url.includes(entry.pattern));
	return productInfo ?? DEFAULT_PRODUCT_INFO;
}

const PRODUCT_LABEL_MESSAGES: Record<SuggestionProductKey, (typeof i18n)[keyof typeof i18n]> = {
	googleDocs: i18n.productGoogleDocs,
	googleSheets: i18n.productGoogleSheets,
	googleSlides: i18n.productGoogleSlides,
	googleDrive: i18n.productGoogleDrive,
	loom: i18n.productLoom,
	figma: i18n.productFigma,
	slack: i18n.productSlack,
	link: i18n.productLink,
};

function getProductLabelMessage(productKey: SuggestionProductKey) {
	return PRODUCT_LABEL_MESSAGES[productKey];
}

const styles = cssMap({
	layout: {
		height: '700px',
		borderRadius: token('radius.xlarge'),
	},
	leftPanel: {
		flexGrow: 1,
		flexBasis: '50%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		overflowY: 'auto',
		paddingBlock: token('space.500'),
		paddingInline: token('space.200'),
	},
	rightPanel: {
		flexGrow: 1,
		flexBasis: '50%',
		backgroundColor: token('color.background.accent.blue.subtler.pressed'),
		overflowY: 'auto',
		position: 'relative',
	},
	previewPanelContent: {
		paddingBlockEnd: token('space.200'),
	},
	previewCard: {
		backgroundColor: token('color.background.input'),
		padding: token('space.150'),
		boxShadow: token('elevation.shadow.overflow'),
	},
	previewGroupsSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.selected'),
		borderRadius: token('radius.medium'),
		padding: token('space.050'),
	},
	previewFoldersList: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	previewFolderExpanded: {
		backgroundColor: token('color.background.input'),
		borderColor: token('color.border.bold'),
	},
	previewFolderPreview: {
		backgroundColor: token('color.background.input'),
		border: 'none',
	},
	previewFolderHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
	},
	previewFolderTitleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		minWidth: '0%',
		flex: 1,
	},
	previewGroupTitle: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	previewFolderBody: {
		display: 'none',
		paddingInline: token('space.150'),
		paddingBlockEnd: token('space.150'),
	},
	previewFolderBodyExpanded: {
		display: 'block',
	},
	previewFolderBodyIndented: {
		paddingInlineStart: token('space.300'),
	},
	previewSuggestionList: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.075'),
	},
	previewSuggestionRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		minWidth: '0%',
	},
	previewSuggestionTitle: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	spaceHeader: {
		paddingBlock: token('space.250'),
		paddingInline: token('space.300'),
		backgroundColor: token('color.background.input'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		alignItems: 'center',
		gap: token('space.100'),
		flexShrink: 0,
	},
	spaceAvatarWrapper: {
		width: '24px',
		height: '24px',
		borderRadius: token('radius.medium'),
		overflow: 'hidden',
		flexShrink: 0,
	},
	stackedContentTypeIconWrapper: {
		marginBlockEnd: token('space.250'),
	},
	closeButtonWrapper: {
		top: token('space.100'),
		left: token('space.100'),
	},
	eyelash: {
		position: 'absolute',
		top: token('space.1000'),
		left: token('space.250'),
		zIndex: 100,
		animation: 'eyelashPop 400ms cubic-bezier(0.25, 0.1, 0.25, 1) forwards',
		pointerEvents: 'none',
	},
	groupListScrollArea: {
		flexGrow: 1,
		overflowY: 'auto',
		scrollbarWidth: 'none',
		minHeight: '0%',
	},
	hoverCard: {
		width: '260px',
		backgroundColor: token('color.background.input'),
		borderRadius: token('radius.large'),
		padding: token('space.150'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	hoverCardContent: {
		maxWidth: '100%',
		minWidth: '0%',
	},
	hoverCardTitle: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		maxWidth: '100%',
	},
	hoverCardDescription: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		// @ts-expect-error - cssMap types do not include this vendor-prefixed property, but it is required for line clamping
		WebkitBoxOrient: 'vertical',
		color: token('color.text.subtle'),
		WebkitLineClamp: '2',
		maxWidth: '100%',
	},
	hoverCardFooter: {
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		marginInline: `var(--ds-space-negative-150)`,
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.100'),
		color: token('color.text.subtle'),
	},
	hoverCardIcon: {
		width: '14px',
		height: '14px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	hoverCardOpenLink: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		textDecoration: 'none',
	},
	container: {
		position: 'absolute',
		top: token('space.600'),
		left: token('space.600'),
		right: token('space.0'),
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.large'),
		overflowX: 'hidden',
	},
	groupCard: {
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		'&:hover': {
			borderColor: token('color.border.bold'),
		},
		transition: 'border-color 150ms ease',
	},
	groupCardExpanded: {
		borderColor: token('color.border.bold'),
		overflow: 'visible',
		position: 'relative',
		zIndex: 0,
	},
	groupCardHovering: {
		position: 'relative',
		zIndex: 700,
		borderColor: token('color.border'),
	},
	expandButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		textAlign: 'start',
		gap: token('space.100'),
		flexGrow: 1,
		paddingBlock: token('space.150'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.150'),
		backgroundColor: 'transparent',
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects token values; transparent needed to inherit parent bg
			backgroundColor: 'transparent',
		},
	},
	groupTitleWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		overflow: 'hidden',
	},
	itemsListOuter: {
		display: 'grid',
		gridTemplateRows: '0fr',
		opacity: 0,
		transition: 'grid-template-rows 350ms ease, opacity 250ms ease',
	},
	itemsListOuterExpanded: {
		gridTemplateRows: '1fr',
		opacity: 1,
	},
	itemsList: {
		overflow: 'visible',
		minHeight: '0%',
	},
	itemRowContainer: {
		position: 'relative',
		zIndex: 0,
	},
	itemRowContainerHovered: {
		zIndex: 1,
	},
	inlineHoverCardWrapper: {
		position: 'absolute',
		top: token('space.500'),
		left: token('space.500'),
		marginTop: token('space.050'),
		opacity: 0,
		pointerEvents: 'none',
		transform: 'translateY(8px)',
		transition: 'opacity 400ms ease, transform 400ms ease',
	},
	inlineHoverCardWrapperVisible: {
		opacity: 1,
		pointerEvents: 'auto',
		transform: 'translateY(0)',
	},
	inlineHoverCardShadowLayer: {
		position: 'relative',
		zIndex: 100,
	},
	itemCheckButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.100'),
		backgroundColor: 'transparent',
		flexShrink: 0,
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects token values; transparent needed to inherit parent bg
			backgroundColor: 'transparent',
		},
	},
	previewPlaceholderSection: {
		paddingInline: token('space.050'),
		display: 'flex',
		flexDirection: 'column',
		paddingBlock: token('space.100'),
	},
	previewPlaceholderRow: {
		display: 'flex',
		alignItems: 'center',
		paddingBlock: token('space.100'),
	},
	previewPlaceholderDot: {
		width: '10px',
		height: '10px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
		flexShrink: 0,
	},
	previewPlaceholderBarLong: {
		height: '8px',
		width: '70%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
	},
	previewPlaceholderBarFull: {
		height: '8px',
		width: '88%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
	},
	previewPlaceholderBarShort: {
		height: '8px',
		width: '55%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
	},
	suggestionLinkWrapper: {
		flexGrow: 1,
		minWidth: '0%',
	},
	suggestionLink: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'nowrap',
		gap: token('space.100'),
		minWidth: '0%',
		paddingBlock: token('space.075'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.150'),
		textDecoration: 'none',
		color: token('color.text'),
		'&:hover': {
			textDecoration: 'none',
		},
	},
	suggestionIconSlot: {
		width: '16px',
		height: '16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	suggestionTitleWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		overflow: 'hidden',
	},
	suggestionLinkExternalIcon: {
		flexShrink: 0,
	},
	addButtonRow: {
		paddingBlockEnd: token('space.200'),
	},
	itemRow: {
		paddingBlock: token('space.100'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects different token values
			backgroundColor: token('color.background.neutral'),
		},
	},
	unselectedOpacity: {
		opacity: token('opacity.disabled'),
	},
	loadingOverlay: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: '560px',
	},
});

const SuggestionHoverCard = ({ suggestion }: { suggestion: Suggestion }) => {
	const { formatMessage } = useIntl();
	const { productKey, iconUrl } = getSuggestionProductInfo(suggestion.url);
	const product = formatMessage(getProductLabelMessage(productKey));
	const description = suggestion.description?.trim()
		? suggestion.description
		: formatMessage(i18n.hoverCardFallbackDescription, { product });

	return (
		<Stack space="space.150" xcss={styles.hoverCard} testId="review-content-suggestion-hover-card">
			<Flex alignItems="center" gap="space.100">
				<Box xcss={styles.hoverCardIcon} aria-hidden="true">
					{iconUrl ? (
						<FallbackableImage
							src={iconUrl}
							width="14"
							height="14"
							fallback={() => <DefaultAppIcon label="" />}
							role="presentation"
						/>
					) : (
						<DefaultAppIcon label="" color={token('color.icon.subtle')} />
					)}
				</Box>
				<Stack space="space.025" xcss={styles.hoverCardContent}>
					<Box xcss={styles.hoverCardTitle}>
						<Text size="small" weight="medium" color="color.text.accent.blue">
							{suggestion.title}
						</Text>
					</Box>
				</Stack>
			</Flex>
			<Box xcss={styles.hoverCardDescription}>
				<Text size="small" color="color.text.subtle">
					{description}
				</Text>
			</Box>
			<Flex alignItems="center" gap="space.100" xcss={styles.hoverCardFooter}>
				<Box xcss={styles.hoverCardIcon} aria-hidden="true">
					{iconUrl ? (
						<FallbackableImage
							src={iconUrl}
							width="14"
							height="14"
							fallback={() => <DefaultAppIcon label="" />}
							role="presentation"
						/>
					) : (
						<DefaultAppIcon label="" color={token('color.icon.subtle')} />
					)}
				</Box>
				<Text size="small" color="color.text.subtle">
					{product}
				</Text>
			</Flex>
		</Stack>
	);
};

const SuggestionItemRow = ({
	suggestion,
	isSelected,
	onToggleSelectItem,
	onHoverCardVisibilityChange,
}: SuggestionItemRowProps) => {
	const { formatMessage } = useIntl();
	const { iconUrl } = getSuggestionProductInfo(suggestion.url);
	const [isHoverCardVisible, setIsHoverCardVisible] = useState(false);

	return (
		<Box
			xcss={cx(styles.itemRowContainer, isHoverCardVisible && styles.itemRowContainerHovered)}
			onMouseEnter={() => {
				setIsHoverCardVisible(true);
				onHoverCardVisibilityChange(true);
			}}
			onMouseLeave={() => {
				setIsHoverCardVisible(false);
				onHoverCardVisibilityChange(false);
			}}
			onFocusCapture={() => {
				setIsHoverCardVisible(true);
				onHoverCardVisibilityChange(true);
			}}
			onBlur={() => {
				setIsHoverCardVisible(false);
				onHoverCardVisibilityChange(false);
			}}
			testId={`review-content-suggestion-row-${suggestion.id}`}
		>
			<Flex alignItems="center" xcss={cx(styles.itemRow, !isSelected && styles.unselectedOpacity)}>
				<SelectToggleIcon
					isSelected={isSelected}
					onClick={() => onToggleSelectItem(suggestion.id)}
					ariaLabel={formatMessage(isSelected ? i18n.deselectItem : i18n.selectItem, {
						title: suggestion.title,
					})}
				/>
				<Box xcss={styles.suggestionLinkWrapper}>
					<Anchor
						href={suggestion.url}
						target="_blank"
						rel="noopener noreferrer"
						aria-label={formatMessage(i18n.openSuggestion, { title: suggestion.title })}
						xcss={styles.suggestionLink}
					>
						<Box xcss={styles.suggestionIconSlot} aria-hidden="true">
							{iconUrl ? (
								<FallbackableImage
									src={iconUrl}
									width="16"
									height="16"
									fallback={() => <DefaultAppIcon label="" />}
									role="presentation"
								/>
							) : (
								<DefaultAppIcon label="" color={token('color.icon.subtle')} />
							)}
						</Box>
						<Box xcss={styles.suggestionTitleWrapper}>
							<Text maxLines={1}>{suggestion.title}</Text>
						</Box>
						<Box xcss={styles.suggestionLinkExternalIcon} aria-hidden="true">
							<LinkExternalIcon label="" color={token('color.icon.subtle')} />
						</Box>
					</Anchor>
				</Box>
			</Flex>
			<Box
				xcss={cx(
					styles.inlineHoverCardWrapper,
					isHoverCardVisible && styles.inlineHoverCardWrapperVisible,
				)}
			>
				<Box xcss={styles.inlineHoverCardShadowLayer}>
					{isHoverCardVisible ? <SuggestionHoverCard suggestion={suggestion} /> : null}
				</Box>
			</Box>
		</Box>
	);
};

const SuggestionGroupRow = ({
	parentTitle,
	suggestions,
	isExpanded,
	isGroupSelected,
	selectedItems,
	toggleExpand,
	parentId,
	toggleSelectGroup,
	onToggleSelectItem,
	onGroupHoverChange,
}: SuggestionGroupRowProps) => {
	const { formatMessage } = useIntl();
	const [isHoverCardVisible, setIsHoverCardVisible] = useState(false);

	// value could change often and doesn't make sense to memoize
	// eslint-disable-next-line @atlassian/perf-linting/no-expensive-computations-in-render
	const selectedSuggestionCount = suggestions.filter((suggestion) =>
		selectedItems.has(suggestion.id),
	).length;

	return (
		<Box
			xcss={cx(
				styles.groupCard,
				isExpanded && styles.groupCardExpanded,
				isHoverCardVisible && styles.groupCardHovering,
			)}
			onMouseEnter={() => onGroupHoverChange(parentId, true)}
			onMouseLeave={() => onGroupHoverChange(parentId, false)}
			onFocusCapture={() => onGroupHoverChange(parentId, true)}
			onBlur={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
					onGroupHoverChange(parentId, false);
				}
			}}
			testId={`review-content-suggestion-group-card-${parentId}`}
		>
			<Flex xcss={!isGroupSelected && styles.unselectedOpacity}>
				<SelectToggleIcon
					isSelected={isGroupSelected}
					onClick={() => toggleSelectGroup(suggestions)}
					ariaLabel={formatMessage(isGroupSelected ? i18n.deselectGroup : i18n.selectGroup, {
						title: parentTitle,
					})}
				/>
				<Pressable
					xcss={styles.expandButton}
					onClick={() => toggleExpand(parentId)}
					aria-expanded={isExpanded}
					aria-label={formatMessage(isExpanded ? i18n.collapseGroup : i18n.expandGroup, {
						title: parentTitle,
					})}
				>
					<Box xcss={styles.groupTitleWrapper}>
						<Text weight="medium" maxLines={1}>
							{parentTitle}
						</Text>
					</Box>
					<Text color="color.text.subtle">{selectedSuggestionCount}</Text>
					{isExpanded ? (
						<ChevronUpIcon label="" color={token('color.icon.subtle')} />
					) : (
						<ChevronDownIcon label="" color={token('color.icon.subtle')} />
					)}
				</Pressable>
			</Flex>
			<Box
				xcss={cx(styles.itemsListOuter, isExpanded && styles.itemsListOuterExpanded)}
				aria-hidden={!isExpanded}
			>
				<Box xcss={styles.itemsList}>
					{suggestions.map((suggestion) => (
						<SuggestionItemRow
							key={suggestion.id}
							suggestion={suggestion}
							isSelected={selectedItems.has(suggestion.id)}
							onToggleSelectItem={onToggleSelectItem}
							onHoverCardVisibilityChange={setIsHoverCardVisible}
						/>
					))}
				</Box>
			</Box>
		</Box>
	);
};

const ModalLeftPanel = ({
	groupedSuggestionsWithTitles,
	selectedItems,
	expandedGroups,
	toggleExpand,
	toggleSelectGroup,
	toggleSelectItem,
	onGroupHoverChange,
}: ModalLeftPanelProps) => {
	const { formatMessage } = useIntl();

	return (
		<Stack space="space.100">
			{[...groupedSuggestionsWithTitles.entries()].map(
				([parentId, { parentTitle, suggestions: groupSuggestions }]) => {
					const isGroupSelected = groupSuggestions.some((s) => selectedItems.has(s.id));
					return (
						<SuggestionGroupRow
							key={parentId}
							parentTitle={parentTitle || formatMessage(i18n.untitled)}
							suggestions={groupSuggestions}
							isExpanded={expandedGroups.has(parentId)}
							isGroupSelected={isGroupSelected}
							selectedItems={selectedItems}
							toggleExpand={toggleExpand}
							parentId={parentId}
							toggleSelectGroup={toggleSelectGroup}
							onToggleSelectItem={toggleSelectItem}
							onGroupHoverChange={onGroupHoverChange}
						/>
					);
				},
			)}
		</Stack>
	);
};

type ReviewContentSuggestionsModalInnerProps = ReviewContentSuggestionsModalProps & {
	flags: FlagsStateContainer;
};

const ReviewContentSuggestionsModalInner = ({
	groups,
	iconUrls,
	spaceKey,
	spaceName,
	iconPath,
	onClose,
	onSaveSuccess,
	flags,
}: ReviewContentSuggestionsModalInnerProps) => {
	const { formatMessage } = useIntl();
	const { Modal } = useChoreographerAssets();

	const allSuggestions = useMemo(() => groups.flatMap((g) => g.remoteObjects), [groups]);

	const [expandedGroups, setExpandedGroups] = useState<Set<number>>(() => new Set());
	const [selectedItems, setSelectedItems] = useState<Set<string>>(
		() => new Set(allSuggestions.map((s) => s.id)),
	);
	const [isHoveringGroupList, setIsHoveringGroupList] = useState(false);
	const [hoveredParentId, setHoveredParentId] = useState<number | null>(null);
	const [previewParentId, setPreviewParentId] = useState<number | undefined>(groups[0]?.parentId);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const parentIds = useMemo(() => groups.map((g) => String(g.parentId)), [groups]);

	const { titlesByContentId, isLoading: isLoadingTitles } = useContentTitles(parentIds);
	const { submitActions } = useSubmitSuggestionActions({ spaceKey, flags });

	const isLoading = isLoadingTitles || isSubmitting;

	const handleAdd = async () => {
		setIsSubmitting(true);
		try {
			const ok = await submitActions(allSuggestions, selectedItems);
			if (ok) {
				await onSaveSuccess();
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const groupedSuggestionsWithTitles = useMemo(() => {
		const result = new Map<number, ParentGrouping>();
		groups.forEach((group) => {
			result.set(group.parentId, {
				parentTitle: titlesByContentId.get(String(group.parentId)) ?? '',
				suggestions: group.remoteObjects,
			});
		});
		return result;
	}, [groups, titlesByContentId]);

	useEffect(() => {
		if (previewParentId === undefined || groups.length === 0) {
			setPreviewParentId(groups[0]?.parentId);
			return;
		}

		if (!groups.some((group) => group.parentId === previewParentId)) {
			setPreviewParentId(groups[0]?.parentId);
		}
	}, [groups, previewParentId]);

	const handleGroupHoverChange = (parentId: number, isHovering: boolean) => {
		setIsHoveringGroupList(isHovering);
		setHoveredParentId(isHovering ? parentId : null);

		if (isHovering) {
			setPreviewParentId(parentId);
		}
	};

	const toggleExpand = (parentId: number) => {
		setExpandedGroups((prev) => {
			const next = new Set(prev);
			if (next.has(parentId)) {
				next.delete(parentId);
			} else {
				next.add(parentId);
			}
			return next;
		});
	};

	const toggleSelectItem = (itemId: string) => {
		setSelectedItems((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
			} else {
				next.add(itemId);
			}
			return next;
		});
	};

	const toggleSelectGroup = (groupSuggestions: Suggestion[]) => {
		const allSelected = groupSuggestions.every((s) => selectedItems.has(s.id));
		setSelectedItems((prev) => {
			const next = new Set(prev);
			groupSuggestions.forEach((s) => {
				if (allSelected) {
					next.delete(s.id);
				} else {
					next.add(s.id);
				}
			});
			return next;
		});
	};

	return (
		<Modal onClose={onClose} width="x-large">
			<ModalBody>
				{isLoading ? (
					<Box xcss={styles.loadingOverlay} testId="review-content-suggestions-modal-loading">
						<Spinner
							size="large"
							label={isSubmitting ? formatMessage(i18n.submitting) : formatMessage(i18n.loading)}
						/>
					</Box>
				) : (
					<Flex xcss={styles.layout}>
						<Box xcss={styles.leftPanel}>
							<Stack space="space.100">
								<Box xcss={styles.stackedContentTypeIconWrapper}>
									<StackedContentTypeIcons iconUrls={iconUrls} variant="large" tilted />
								</Box>
								<Heading size="medium">{formatMessage(i18n.bringTogetherTitle)}</Heading>
								<Text color="color.text.subtle">
									{formatMessage(i18n.bringTogetherDescription)}
								</Text>
							</Stack>
							<Box xcss={styles.groupListScrollArea}>
								<ModalLeftPanel
									groupedSuggestionsWithTitles={groupedSuggestionsWithTitles}
									selectedItems={selectedItems}
									expandedGroups={expandedGroups}
									toggleExpand={toggleExpand}
									toggleSelectGroup={toggleSelectGroup}
									toggleSelectItem={toggleSelectItem}
									onGroupHoverChange={handleGroupHoverChange}
								/>
							</Box>
							<Flex justifyContent="end" xcss={styles.addButtonRow}>
								<Button
									appearance="primary"
									onClick={() => void handleAdd()}
									isDisabled={selectedItems.size === 0}
								>
									{formatMessage(i18n.addItemsToSpace, { count: selectedItems.size })}
								</Button>
							</Flex>
						</Box>
						<Box xcss={styles.rightPanel}>
							<Box xcss={styles.closeButtonWrapper}>
								<ModalHeader hasCloseButton />
							</Box>
							{isHoveringGroupList && hoveredParentId !== null ? (
								<svg
									key={`eyelash-${previewParentId}`}
									xmlns="http://www.w3.org/2000/svg"
									width="68"
									height="72"
									viewBox="0 0 79 84"
									fill="none"
									data-testid="review-content-suggestions-modal-eyelash"
									css={styles.eyelash}
								>
									<path
										d="M46.8305 18.5258C47.1098 17.9661 43.6268 8.90741 42.7488 11.521C42.2936 12.4058 42.5248 13.2117 43.5174 13.4554C41.7549 13.774 44.5278 15.4349 44.1037 16.2809C42.7767 16.0236 45.925 18.4365 44.5093 17.9953C45.1375 27.8747 61.4539 47.3269 49.1561 23.3778C49.0744 25.796 48.1128 23.545 48.1739 22.458C50.1084 23.9562 47.7869 19.4885 47.0583 18.6728C47.3738 20.0681 45.7105 17.7992 46.8271 18.5301L46.8305 18.5258Z"
										fill="#101214"
									/>
									<path
										d="M35.0784 36.7323C34.7386 35.4516 24.741 30.304 23.7564 29.6503C21.4451 28.5916 21.8766 31.5473 23.7521 31.9823C24.1049 32.2151 26.8063 32.9863 25.8424 33.6122C26.9098 36.11 31.5063 36.3441 32.2162 38.4572C32.7437 38.1031 33.6064 38.8355 33.9705 38.6541C34.0289 39.2864 34.9612 39.616 35.0079 39.4628C37.7835 42.0995 39.7426 38.6927 36.245 37.3269C36.3602 37.2145 35.6467 36.4956 35.0869 36.7321L35.0784 36.7323Z"
										fill="#101214"
									/>
									<path
										d="M24.963 69.8221C26.6738 70.9367 25.0975 70.8963 24.267 70.3448C25.383 69.2761 22.2883 69.3545 21.5652 69.7215C21.8995 69.7773 22.0611 70.3228 21.3734 70.2454C21.7268 68.9194 13.0408 68.0384 17.3812 71.1509C17.5287 71.156 17.4988 70.0879 17.7021 70.4339C17.5556 72.0383 18.8157 71.0002 19.1881 70.9031C19.2546 71.7465 19.5499 70.9879 19.6776 71.344C19.3094 72.4465 21.3534 70.7021 20.9218 71.7891C23.0595 73.6544 26.7834 71.6524 28.4396 73.3301C29.3174 71.5064 35.9199 74.7917 33.1474 70.9933C30.3368 69.9795 28.0535 69.3594 24.963 69.8221Z"
										fill="#101214"
									/>
									<path
										d="M6.74898 49.2677C6.29473 51.8253 12.2062 51.6791 13.9219 52.8612C16.019 54.5247 37.5618 61.2955 31.6306 55.7815C25.1019 53.783 19.9198 51.0908 11.8547 49.5917C10.5468 49.258 6.41403 47.4335 6.74898 49.2677Z"
										fill="#101214"
									/>
									<path
										d="M68.2125 30.6354C70.56 32.2805 68.0111 34.2349 69.9592 35.7792C73.6428 38.1546 73.0128 18.9394 69.4953 19.3145C66.5877 20.0435 69.3925 29.3498 68.2125 30.6354Z"
										fill="#101214"
									/>
								</svg>
							) : null}
							<Box xcss={styles.container}>
								<Flex xcss={styles.spaceHeader}>
									<Box xcss={styles.spaceAvatarWrapper}>
										<FallbackableImage
											src={iconPath || ''}
											width="24"
											height="24"
											alt={formatMessage(i18n.spaceAvatarAlt, {
												spaceName: spaceName || formatMessage(i18n.untitled),
											})}
											fallback={() => (
												<DefaultAppIcon
													label={formatMessage(i18n.spaceAvatarAlt, {
														spaceName: spaceName || formatMessage(i18n.untitled),
													})}
												/>
											)}
										/>
									</Box>
									<Box xcss={styles.spaceHeaderTitle}>
										<Text weight="medium">{spaceName || formatMessage(i18n.untitled)}</Text>
									</Box>
								</Flex>
								<Box xcss={styles.previewPanelContent}>
									<Box xcss={styles.previewCard}>
										<Stack space="space.100" xcss={styles.previewFoldersList}>
											<Stack
												xcss={styles.previewPlaceholderSection}
												testId="review-content-suggestions-modal-preview-placeholder-section"
											>
												<Flex xcss={styles.previewPlaceholderRow}>
													<Box xcss={styles.previewPlaceholderDot} />
													<Box xcss={styles.previewPlaceholderBarLong} />
												</Flex>
												<Box xcss={styles.previewPlaceholderBarFull} />
											</Stack>
											<Box
												xcss={styles.previewGroupsSection}
												testId="review-content-suggestions-modal-preview-groups-section"
											>
												{[...groupedSuggestionsWithTitles.entries()].map(
													([parentId, { parentTitle, suggestions: groupSuggestions }]) => {
														const isFolderExpanded = previewParentId === parentId;
														return (
															<Box key={parentId} xcss={cx(styles.previewFolderPreview)}>
																<Box xcss={styles.previewFolderHeader}>
																	<Box xcss={styles.previewFolderTitleRow}>
																		{isFolderExpanded ? (
																			<FolderOpenIcon label="" color={token('color.icon.subtle')} />
																		) : (
																			<FolderClosedIcon
																				label=""
																				color={token('color.icon.subtle')}
																			/>
																		)}
																		<Box xcss={styles.previewGroupTitle}>
																			<Text weight="medium" maxLines={1}>
																				{parentTitle || formatMessage(i18n.untitled)}
																			</Text>
																		</Box>
																	</Box>
																</Box>
																<Box
																	xcss={cx(
																		styles.previewFolderBody,
																		styles.previewFolderBodyIndented,
																		isFolderExpanded && styles.previewFolderBodyExpanded,
																	)}
																>
																	<Stack space="space.050" xcss={styles.previewSuggestionList}>
																		{groupSuggestions.map((suggestion) => {
																			const { iconUrl } = getSuggestionProductInfo(suggestion.url);
																			return (
																				<Flex
																					key={suggestion.id}
																					alignItems="center"
																					xcss={styles.previewSuggestionRow}
																				>
																					<Box xcss={styles.suggestionIconSlot} aria-hidden="true">
																						{iconUrl ? (
																							<FallbackableImage
																								src={iconUrl}
																								width="16"
																								height="16"
																								fallback={() => <DefaultAppIcon label="" />}
																								role="presentation"
																							/>
																						) : (
																							<DefaultAppIcon
																								label=""
																								color={token('color.icon.subtle')}
																							/>
																						)}
																					</Box>
																					<Box xcss={styles.previewSuggestionTitle}>
																						<Text maxLines={1}>{suggestion.title}</Text>
																					</Box>
																				</Flex>
																			);
																		})}
																	</Stack>
																</Box>
															</Box>
														);
													},
												)}
											</Box>
											<Stack
												xcss={styles.previewPlaceholderSection}
												testId="review-content-suggestions-modal-preview-placeholder-section"
											>
												<Flex xcss={styles.previewPlaceholderRow}>
													<Box xcss={styles.previewPlaceholderDot} />
													<Box xcss={styles.previewPlaceholderBarLong} />
												</Flex>
												<Box xcss={styles.previewPlaceholderBarFull} />
											</Stack>
										</Stack>
									</Box>
								</Box>
							</Box>
						</Box>
					</Flex>
				)}
			</ModalBody>
		</Modal>
	);
};

export const ReviewContentSuggestionsModal = (props: ReviewContentSuggestionsModalProps) => (
	<Subscribe to={[FlagsStateContainer]}>
		{(flags: FlagsStateContainer) => (
			<ReviewContentSuggestionsModalInner {...props} flags={flags} />
		)}
	</Subscribe>
);
